import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  createDoorVisitCoreApi,
  isNonBlankDoorVisitCoreString,
  type DoorVisitCoreApi,
  type DoorVisitCoreFetchImpl,
} from './doorVisitCoreApi';

export type DoorVisitPlaybackDetail = {
  doorVisitId: number;
  coreLeadId?: string | null;
  coreCallId?: string | null;
};

export type DoorVisitPlaybackIdentity = {
  routeDoorVisitId: number;
  doorVisitId: number;
  coreLeadId: string;
  coreCallId: string;
};

export type DoorVisitPlaybackState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; playbackUrl: string }
  | { status: 'unavailable' };

export type UseDoorVisitPlaybackOptions = {
  routeDoorVisitId: number;
  detail: DoorVisitPlaybackDetail | null;
  fetchImpl?: DoorVisitCoreFetchImpl;
  api?: Pick<DoorVisitCoreApi, 'getPlayback'>;
};

export type UseDoorVisitPlaybackResult = {
  eligible: boolean;
  state: DoorVisitPlaybackState;
  requestPlayback: () => void;
  reportMediaError: () => void;
  bindAudio: (node: HTMLAudioElement | null) => void;
};

const IDLE_PLAYBACK_STATE: DoorVisitPlaybackState = { status: 'idle' };

const samePlaybackIdentity = (
  left: DoorVisitPlaybackIdentity | null,
  right: DoorVisitPlaybackIdentity | null,
): boolean => {
  if (left === null || right === null) return left === right;

  return (
    left.routeDoorVisitId === right.routeDoorVisitId &&
    left.doorVisitId === right.doorVisitId &&
    left.coreLeadId === right.coreLeadId &&
    left.coreCallId === right.coreCallId
  );
};

const resolvePlaybackIdentity = (
  routeDoorVisitId: number,
  detail: DoorVisitPlaybackDetail | null,
): DoorVisitPlaybackIdentity | null => {
  if (detail === null || detail.doorVisitId !== routeDoorVisitId) return null;
  if (!isNonBlankDoorVisitCoreString(detail.coreLeadId) || !isNonBlankDoorVisitCoreString(detail.coreCallId)) {
    return null;
  }

  return {
    routeDoorVisitId,
    doorVisitId: detail.doorVisitId,
    coreLeadId: detail.coreLeadId,
    coreCallId: detail.coreCallId,
  };
};

const disposeAudioElement = (audio: HTMLAudioElement): void => {
  audio.pause();
  audio.removeAttribute('src');
  audio.load();
};

export const useDoorVisitPlayback = ({
  routeDoorVisitId,
  detail,
  fetchImpl,
  api: apiOverride,
}: UseDoorVisitPlaybackOptions): UseDoorVisitPlaybackResult => {
  const api = useMemo(
    () => apiOverride ?? createDoorVisitCoreApi({ fetchImpl }),
    [apiOverride, fetchImpl],
  );
  const identity = resolvePlaybackIdentity(routeDoorVisitId, detail);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const generationRef = useRef(0);
  const inFlightRef = useRef(false);
  const suppressMediaErrorRef = useRef(false);
  const identityRef = useRef(identity);
  const [trackedIdentity, setTrackedIdentity] = useState(identity);
  const [state, setState] = useState<DoorVisitPlaybackState>(IDLE_PLAYBACK_STATE);

  // Identity is part of the playback scope. Invalidate synchronously so a playback
  // promise that resolves in the same turn cannot commit a URL onto another visit.
  identityRef.current = identity;
  if (!samePlaybackIdentity(trackedIdentity, identity)) {
    generationRef.current += 1;
    inFlightRef.current = false;
    const controller = controllerRef.current;
    controllerRef.current = null;
    controller?.abort();
    setTrackedIdentity(identity);
    setState(IDLE_PLAYBACK_STATE);
  }

  const bindAudio = useCallback((node: HTMLAudioElement | null): void => {
    if (node === null) {
      const current = audioRef.current;
      if (current !== null) {
        suppressMediaErrorRef.current = true;
        disposeAudioElement(current);
      }
      audioRef.current = null;
      return;
    }

    suppressMediaErrorRef.current = false;
    audioRef.current = node;
  }, []);

  const reportMediaError = useCallback((): void => {
    if (suppressMediaErrorRef.current || identityRef.current === null) return;

    suppressMediaErrorRef.current = true;
    generationRef.current += 1;
    inFlightRef.current = false;
    const controller = controllerRef.current;
    controllerRef.current = null;
    controller?.abort();
    if (audioRef.current !== null) disposeAudioElement(audioRef.current);
    setState({ status: 'unavailable' });
  }, []);

  const playbackUrl = state.status === 'ready' ? state.playbackUrl : null;

  useEffect(() => {
    if (playbackUrl === null) return undefined;

    const audio = audioRef.current;
    if (audio === null) return undefined;

    audio.focus();
    try {
      const playResult: unknown = audio.play();
      if (playResult instanceof Promise) {
        void playResult.catch(() => undefined);
      }
    } catch {
      // A browser may throw while applying its autoplay policy. Native controls stay usable.
    }

    return undefined;
  }, [playbackUrl]);

  useEffect(() => {
    return () => {
      generationRef.current += 1;
      inFlightRef.current = false;
      const controller = controllerRef.current;
      controllerRef.current = null;
      controller?.abort();
      const audio = audioRef.current;
      audioRef.current = null;
      if (audio !== null) {
        suppressMediaErrorRef.current = true;
        disposeAudioElement(audio);
      }
    };
  }, []);

  const requestPlayback = useCallback((): void => {
    const requestIdentity = identityRef.current;
    if (requestIdentity === null || inFlightRef.current) return;

    inFlightRef.current = true;
    const generation = ++generationRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    setState({ status: 'loading' });

    let request: ReturnType<DoorVisitCoreApi['getPlayback']>;
    try {
      request = api.getPlayback(
        { leadId: requestIdentity.coreLeadId, callId: requestIdentity.coreCallId },
        { signal: controller.signal },
      );
    } catch {
      request = Promise.resolve({ kind: 'error' });
    }

    const acceptResult = (): boolean =>
      generation === generationRef.current && samePlaybackIdentity(requestIdentity, identityRef.current);

    void request
      .then(result => {
        if (!acceptResult()) return;

        controllerRef.current = null;
        inFlightRef.current = false;
        if (result.kind === 'aborted' || controller.signal.aborted) return;

        if (result.kind === 'available' && isNonBlankDoorVisitCoreString(result.playbackUrl)) {
          setState({ status: 'ready', playbackUrl: result.playbackUrl.trim() });
          return;
        }

        setState({ status: 'unavailable' });
      })
      .catch(() => {
        if (!acceptResult()) return;

        controllerRef.current = null;
        inFlightRef.current = false;
        if (controller.signal.aborted) return;
        setState({ status: 'unavailable' });
      });
  }, [api]);

  return {
    eligible: identity !== null,
    state,
    requestPlayback,
    reportMediaError,
    bindAudio,
  };
};
